import React, { useState, useEffect } from "react";
import { Card, Input, Space, Button, Row, Col, message, Spin, Typography } from "antd";
import { UserOutlined, LockOutlined, SafetyCertificateOutlined, MailOutlined } from "@ant-design/icons";
import { api } from "../../config/api";
import { Link, useParams } from "react-router-dom";
import { destroyAuthToken, getAuthToken } from "../../helpers/account";
import { isPasswordSafe } from "../../helpers/validators";
import { Helmet } from "react-helmet-async";

const { Title, Paragraph } = Typography;

const AcceptInvitationPage = () => {
  const [email, setEmail] = useState("");
  const [emailConfirmed, setEmailConfirmed] = useState(false);
  const [codeVerifed, setCodeVerifed] = useState(false);
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(true);
  const [code, setCode] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [messageApi, contextHolder] = message.useMessage();
  const { invitationUuid } = useParams();

  useEffect(() => {
    const apiUrl = `${api.host}/accounts/find-email-by-invitation-uuid`;
    const requestBody = { uuid: invitationUuid };

    fetch(apiUrl, {
      method: "POST",
      headers: new Headers({
        "Content-Type": "application/json",
      }),
      body: JSON.stringify(requestBody),
    })
      .then((response) => response.json())
      .then((data) => {
        setEmail(data.email);
        setLoading(false);
      })
      .catch((error) => {
        messageApi.open({
          type: "error",
          content: "Zaproszenie nie zostało odnalezione",
        });
        setLoading(false);
      });
  }, [invitationUuid]);

  const handleSendCode = () => {
    const apiUrl = `${api.host}/accounts/initiate-email-confirmation`;
    const requestBody = { email };

    fetch(apiUrl, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(requestBody),
    })
      .then((response) => {
        if (response.status === 200) {
          response
            .json()
            .then((data) => {
              setEmailConfirmed(true);
              messageApi.open({
                type: "success",
                content: "Kod autoryzujący został wysłany na adres e-mail",
              });
            })
            .catch((error) => {
              messageApi.open({
                type: "error",
                content: "Wystąpił błąd w akcji generowania kodu autoryzującego",
              });
            });
        } else {
          messageApi.open({
            type: "warning",
            content: "Błędny adres e-mail",
          });
          destroyAuthToken();
        }
      })
      .catch((error) => {
        messageApi.open({
          type: "warning",
          content: "Błędny adres e-mail",
        });
        destroyAuthToken();
      });
  };

  const handleVerification = () => {
    if (code == "") {
      messageApi.open({
        type: "error",
        content: "Nie przepisałeś kodu weryfikacyjnego.",
      });
      return;
    }

    const apiUrl = `${api.host}/accounts/email-confirmation`;
    const requestBody = { email, code };

    fetch(apiUrl, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(requestBody),
    })
      .then((response) => {
        if (response.status === 200) {
          setCodeVerifed(true);
          response
            .json()
            .then((data) => {
              messageApi.open({
                type: "success",
                content: "Konto zostało zweryfikowane.",
              });
              sessionStorage.setItem("authToken", data.authToken);
              sessionStorage.setItem("uuid", data.uuid);
            })
            .catch((error) => {});
        }
      })
      .catch((error) => {
        messageApi.open({
          type: "error",
          content: "Niepoprawny kod weryfikacyjny. Spróbuj jeszcze raz.",
        });
      });
  };

  const handleReset = () => {
    if (password !== confirmPassword) {
      messageApi.open({
        type: "error",
        content: "Hasła nie pasują do siebie. Spróbuj ponownie.",
      });
      return;
    }

    if (!isPasswordSafe(password)) {
      messageApi.open({
        type: "error",
        content: "Hasło musi mieć co najmniej 8 znaków i zawierać przynajmniej jedną wielką literę, jedną cyfrę i jeden znak specjalny (!@#$%^&*).",
      });
      return;
    }

    const apiUrl = `${api.host}/accounts/reset-password`;
    const requestBody = { newPassword: password };

    fetch(apiUrl, {
      method: "POST",
      headers: new Headers({
        "Content-Type": "application/json",
        Authorization: `${getAuthToken()}`,
      }),
      body: JSON.stringify(requestBody),
    })
      .then((response) => {
        if (response.status === 200) {
          messageApi.open({
            type: "success",
            content: "Hasło do konta zostało ustawione.",
          });
        }
        destroyAuthToken();
        setTimeout(() => {
          window.location.href = "/transactions";
        }, 2000);
      })
      .catch((error) => {
        messageApi.open({
          type: "error",
          content: "Wystąpiły błędy podczas akcji akceptacji zaproszenia.",
        });
        destroyAuthToken();
        setTimeout(() => {
          window.location.href = "/transactions";
        }, 2000);
      });
  };

  return (
    <>
      <Helmet>
        <title>Akceptacja zaproszenia - Taskizy</title>
      </Helmet>
      
      <section style={{ 
        minHeight: "100vh", 
        display: "flex", 
        alignItems: "center", 
        background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
        padding: "40px 20px"
      }}>
        {contextHolder}
        <div style={{ maxWidth: "1200px", margin: "0 auto", width: "100%" }}>
          <Row gutter={[48, 48]} align="middle">
            <Col xs={24} lg={12}>
              <div style={{ color: "white", textAlign: { xs: "center", lg: "left" } }}>
                <Title level={1} style={{ 
                  color: "white", 
                  fontSize: "clamp(2rem, 5vw, 3.5rem)", 
                  marginBottom: "24px" 
                }}>
                  Witamy w zespole!
                </Title>
                <Paragraph style={{ 
                  fontSize: "clamp(1rem, 2.5vw, 1.25rem)", 
                  color: "rgba(255,255,255,0.9)",
                  marginBottom: "40px"
                }}>
                  Zostałeś zaproszony do Taskizy. Dokończ proces rejestracji, aby rozpocząć współpracę.
                </Paragraph>
                <div style={{ fontSize: "clamp(60px, 12vw, 120px)", lineHeight: "1", padding: "20px 0" }}>
                  🎉
                </div>
              </div>
            </Col>
            
            <Col xs={24} lg={12}>
              <div style={{ display: "flex", justifyContent: "center" }}>
                <Spin spinning={loading}>
                  <Card
                    style={{
                      width: "100%",
                      maxWidth: "400px",
                      border: "none",
                      boxShadow: "0 20px 40px rgba(0,0,0,0.1)",
                      borderRadius: "12px"
                    }}
                  >
                    <Space direction="vertical" size="large" style={{ width: "100%" }}>
                      <div style={{ textAlign: "center", marginBottom: "20px" }}>
                        <Title level={3} style={{ margin: 0, color: "#2c3e50" }}>
                          Akceptacja zaproszenia
                        </Title>
                        <Paragraph style={{ color: "#6c757d", margin: "8px 0 0 0" }}>
                          {!emailConfirmed && "Potwierdź swój adres e-mail"}
                          {emailConfirmed && !codeVerifed && "Wprowadź kod weryfikacyjny"}
                          {codeVerifed && "Ustaw hasło do konta"}
                        </Paragraph>
                      </div>

                      <Input
                        size="large"
                        autoComplete="off"
                        placeholder="Adres e-mail"
                        prefix={<UserOutlined style={{ color: "#6c757d" }} />}
                        value={email}
                        disabled={true}
                        style={{ borderRadius: "8px", backgroundColor: "#f8f9fa" }}
                      />

                      {emailConfirmed && !codeVerifed && (
                        <>
                          <div style={{ 
                            background: "#f8f9fa", 
                            padding: "16px", 
                            borderRadius: "8px",
                            border: "1px solid #e9ecef"
                          }}>
                            <Paragraph style={{ margin: 0, fontSize: "14px", color: "#6c757d" }}>
                              Przepisz otrzymany kod weryfikacyjny, który wysłaliśmy na podany adres e-mail.
                            </Paragraph>
                          </div>
                          <Input
                            size="large"
                            autoComplete="off"
                            placeholder="Kod weryfikacyjny"
                            prefix={<SafetyCertificateOutlined style={{ color: "#6c757d" }} />}
                            value={code}
                            onChange={(e) => setCode(e.target.value)}
                            style={{ borderRadius: "8px" }}
                          />
                          <Button 
                            type="primary" 
                            size="large" 
                            onClick={handleVerification}
                            style={{ 
                              width: "100%", 
                              height: "48px",
                              borderRadius: "8px",
                              fontSize: "16px",
                              fontWeight: "500"
                            }}
                          >
                            Zweryfikuj adres e-mail
                          </Button>
                        </>
                      )}

                      {codeVerifed && (
                        <>
                          <Input.Password
                            size="large"
                            autoComplete="off"
                            placeholder="Hasło"
                            prefix={<LockOutlined style={{ color: "#6c757d" }} />}
                            value={password}
                            onChange={(e) => setPassword(e.target.value)}
                            style={{ borderRadius: "8px" }}
                          />
                          <Input.Password
                            size="large"
                            autoComplete="off"
                            placeholder="Powtórz hasło"
                            prefix={<LockOutlined style={{ color: "#6c757d" }} />}
                            value={confirmPassword}
                            onChange={(e) => setConfirmPassword(e.target.value)}
                            style={{ borderRadius: "8px" }}
                          />
                          <Button 
                            type="primary" 
                            size="large" 
                            onClick={handleReset}
                            style={{ 
                              width: "100%", 
                              height: "48px",
                              borderRadius: "8px",
                              fontSize: "16px",
                              fontWeight: "500"
                            }}
                          >
                            Ustaw hasło do konta
                          </Button>
                        </>
                      )}

                      {!emailConfirmed && !codeVerifed && (
                        <Button 
                          type="primary" 
                          size="large" 
                          onClick={handleSendCode}
                          icon={<MailOutlined />}
                          style={{ 
                            width: "100%", 
                            height: "48px",
                            borderRadius: "8px",
                            fontSize: "16px",
                            fontWeight: "500"
                          }}
                        >
                          Wyślij kod na e-mail
                        </Button>
                      )}
                    </Space>
                  </Card>
                </Spin>
              </div>
            </Col>
          </Row>
        </div>
      </section>
    </>
  );
};

export default AcceptInvitationPage;